/**
 * printableVerdict — marka tutarlı, baskı/PDF dostu HTML çıktı üreticisi.
 *
 * Strateji:
 *   • Bir blob:// URL'sine self-contained HTML yazılır (CSS dahil, font CDN
 *     opsiyonel). Yeni sekmede açılır → tarayıcı "Print" diyaloğunu otomatik
 *     tetikler → kullanıcı "PDF olarak kaydet" seçer.
 *   • Hiçbir external dependency YOK (jsPDF + html2canvas yığını gereksiz).
 *   • Marka: DAI Consensus logo, accent renk, typografik hiyerarşi.
 */
import type {
  ActionItem,
  ActionPlanV2,
  KpiEntry,
  Persona,
  RiskEntry,
  Turn,
  Verdict,
} from "../domain/types";
import {
  coerceActionPlan,
  coerceConsistencyFlags,
  coerceCoverageMatrix,
  coerceEntityGraph,
  coerceInvestigationPlan,
} from "../domain/types";
import type {
  DebateAggregateMetrics,
  FlatRagCitation,
  FlatResearchSource,
} from "../store/selectors";
import { AGENFAB_LOGO_SVG, CRUDFAB_LOGO_DATA_URI } from "./brandAssets";

export interface VerdictReportInput {
  query: string;
  topology: string;
  startedAt: number | null;
  verdict: Verdict;
  personas: Persona[];
  turns: Turn[];
  /** Aggregate metrics — feeds the extended header grid. */
  metrics?: DebateAggregateMetrics;
  /** Flat research sources list — feeds the bibliography section. */
  sources?: FlatResearchSource[];
  /** RAG document citations — feeds the "Belge Atıfları" section. */
  ragCitations?: FlatRagCitation[];
  /** Final consensus score (0..1). */
  consensusScore?: number;
  /**
   * Konsey Cognitive Renaissance — Bölüm D6.
   * Per-persona AgentBrain final snapshot raporu. Yoksa Section 12
   * tamamen render edilmez (legacy verdict'ler kırılmaz).
   */
  brainProfiles?: BrainProfileForReport[];
  /**
   * Sorunun sihirbazda ölçülen kalite puanı (0-100). Rapora yazılır ki
   * yöneticinin eline geçen analizin, kalitesiz bir soru üzerine kurulup
   * kurulmadığı görünsün.
   */
  questionQuality?: { score: number; verdict?: string } | null;
  /**
   * Hangi rapor üretilecek:
   *   `executive` — karar, plan, riskler, muhalefet, yorum (2-3 sayfa)
   *   `detail`    — konuşma dağılımı, üyeler, kaynakça, transkript
   *   `full`      — ikisi arka arkaya (geriye dönük varsayılan)
   */
  mode?: ReportMode;
}

export type ReportMode = "executive" | "detail" | "full";

/**
 * Konsey Cognitive Renaissance — Bölüm D6.
 * brainSlice.brainSnapshots'dan PDF rapora geçirilen kompakt şekil.
 */
export interface BrainProfileForReport {
  personaId: string;
  personaName: string;
  /** Bilgi büyümesi: (end - baseline) / baseline. */
  learningCoefficient: number;
  /** temporal × prefrontal sinerjisi. */
  interpretationConsistency: number;
  /** Cerebellum avg latency (ms). */
  avgNeuralLatencyMs: number;
  /** Tartışma boyunca ulaşılan zirve cognitive load (0-100). */
  peakCognitiveLoadPct: number;
  /** Amygdala heat ortalaması (0-1). */
  avgAmygdalaHeat: number;
  /** Composite üretkenlik 0-100. */
  productivityScore: number;
  /** Son snapshot — tüm lob alanları. */
  finalSnapshot?: Record<string, unknown> | null;
}

const TOPOLOGY_LABELS: Record<string, string> = {
  "round-robin": "Tur-Tur",
  "cross-fire": "Çapraz Ateş",
  "moderator-led": "Moderatör Liderliğinde",
  "diverse-poles": "Karşıt Kutuplar",
  "solo-then-debate": "Önce Solo / Sonra Tartışma",
};

const SEVERITY_LABELS: Record<string, string> = {
  minor: "Hafif",
  major: "Önemli",
  blocker: "Engelleyici",
};

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function fmtDate(ms: number): string {
  return new Date(ms).toLocaleString("tr-TR", {
    year: "numeric",
    month: "long",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function durationLabel(start: number | null, end: number): string {
  if (!start) return "—";
  const sec = Math.max(0, Math.round((end - start) / 1000));
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m} dk ${s} sn` : `${s} sn`;
}

/* ────────────────────────────────────────────────────────────────────
   2026 yapısal eylem planı renderları — McKinsey 3-Horizons + SMART
   + RACI çerçevesini PDF'e taşır. Eski string[] payload'ları
   coerceActionPlan ile yapısal satırlara dönüştürülür.
   ──────────────────────────────────────────────────────────────────── */

function actionItemsHtml(items: ActionItem[]): string {
  if (items.length === 0) {
    return `<p class="empty">Bu kademede aksiyon önerilmedi.</p>`;
  }
  return `<ol class="action-items">${items
    .map((it) => {
      const pri = it.priority ?? "P1";
      const eff = it.effort ?? "M";
      const meta: string[] = [];
      if (it.owner) meta.push(`<strong>Sahip:</strong> ${escapeHtml(it.owner)}`);
      if (it.accountable && it.accountable !== it.owner)
        meta.push(`<strong>Onay:</strong> ${escapeHtml(it.accountable)}`);
      if (it.deadlineWindow)
        meta.push(`<strong>Hedef:</strong> ${escapeHtml(it.deadlineWindow)}`);
      if (it.successMetric && it.successMetric !== "—")
        meta.push(
          `<strong>Metrik:</strong> ${escapeHtml(it.successMetric)}`,
        );
      const metaLine = meta.length
        ? `<div class="ai-meta">${meta.join(" · ")}</div>`
        : "";
      const rationale = it.rationale
        ? `<div class="ai-rationale">${escapeHtml(it.rationale)}</div>`
        : "";
      const deps =
        it.dependencies && it.dependencies.length
          ? `<div class="ai-deps"><em>Bağımlılık:</em> ${it.dependencies
              .map(escapeHtml)
              .join(", ")}</div>`
          : "";
      // Konsey Cognitive Renaissance — Bölüm C3.
      // Per-item risks artık render edilir. ActionItem.risks string[]
      // (her satır kısa risk özeti). Boşsa hiç render etme.
      const itemRisks =
        it.risks && it.risks.length > 0
          ? `<div class="ai-item-risks">
               <em>Riskler:</em>
               <ul>
                 ${it.risks.map((r) => `<li>${escapeHtml(r)}</li>`).join("")}
               </ul>
             </div>`
          : "";
      return `<li>
        <div class="ai-head">
          <span class="ai-pri ai-pri-${pri}">${pri}</span>
          <span class="ai-eff">${eff}</span>
          <span class="ai-title">${escapeHtml(it.title)}</span>
        </div>
        ${rationale}
        ${metaLine}
        ${deps}
        ${itemRisks}
      </li>`;
    })
    .join("")}</ol>`;
}

function risksHtml(risks: RiskEntry[]): string {
  if (risks.length === 0) return "";
  return `<table class="risk-table">
    <thead><tr><th>Risk</th><th>Olasılık</th><th>Etki</th><th>Mitigasyon</th><th>Sahip</th></tr></thead>
    <tbody>${risks
      .map(
        (r) => `<tr>
        <td>${escapeHtml(r.risk)}</td>
        <td><span class="risk-level risk-${r.likelihood ?? "medium"}">${r.likelihood ?? "medium"}</span></td>
        <td><span class="risk-level risk-${r.impact ?? "medium"}">${r.impact ?? "medium"}</span></td>
        <td>${escapeHtml(r.mitigation)}</td>
        <td>${escapeHtml(r.owner ?? "—")}</td>
      </tr>`,
      )
      .join("")}</tbody>
  </table>`;
}

function kpisHtml(kpis: KpiEntry[]): string {
  if (kpis.length === 0) return "";
  return `<table class="kpi-table">
    <thead><tr><th>Metrik</th><th>Hedef</th><th>Kadans</th></tr></thead>
    <tbody>${kpis
      .map(
        (k) => `<tr>
        <td>${escapeHtml(k.metric)}</td>
        <td>${escapeHtml(k.target)}</td>
        <td>${escapeHtml(k.cadence ?? "—")}</td>
      </tr>`,
      )
      .join("")}</tbody>
  </table>`;
}

function assumptionsHtml(items: string[]): string {
  if (items.length === 0) return "";
  return `<ul class="assumptions">${items
    .map((a) => `<li>${escapeHtml(a)}</li>`)
    .join("")}</ul>`;
}

/**
 * Sorunun kalite puanı, sorunun hemen altında.
 *
 * Bir analiz koşusunu başlatan personel ile raporu okuyan yönetici aynı
 * kişi değil. Yönetici, elindeki kararın ne kadar iyi tanımlanmış bir
 * soru üzerine kurulduğunu göremiyordu; muğlak bir soruyla üretilmiş
 * rapor da aynı ciddiyette görünüyordu.
 */
function questionQualityHtml(
  q: { score: number; verdict?: string } | null | undefined,
): string {
  if (!q || typeof q.score !== "number") return "";
  const score = Math.round(q.score);
  const band =
    score >= 80
      ? { label: "Soru iyi tanımlanmış", color: "var(--severity-minor)" }
      : score >= 60
        ? { label: "Soru kabul edilebilir", color: "var(--severity-major)" }
        : { label: "Soru zayıf tanımlanmış", color: "var(--severity-major)" };
  const caveat =
    score < 60
      ? " Bu puan düşükken üretilen analiz, sorunun boşluklarını " +
        "varsayımlarla doldurmuş olabilir; kararı buna göre değerlendirin."
      : "";
  return `<div class="qq-band" style="border-color:${band.color}">
    <span class="qq-score" style="color:${band.color}">${score}<span class="qq-unit">/100</span></span>
    <span class="qq-text"><strong>Soru kalitesi:</strong> ${band.label}.
    Analiz başlatılmadan önce sorunun somutluk, ölçülebilirlik, kapsam ve
    karar verilebilirlik boyutlarında değerlendirilmesiyle hesaplandı.${caveat}</span>
  </div>`;
}

/**
 * Risk register eylem planı ekinde DEĞİL, "Riskler ve Plan Boşlukları"
 * bölümünde basılır. Operatörün gözlemi yerindeydi: aynı raporda hem 12
 * kırılgan senaryo hem 12 satırlık risk tablosu vardı ve ikisi ayrı ayrı
 * okunduğunda kalabalık, birlikte okunduğunda mükerrer görünüyordu.
 */
const SUPPRESS_PLAN_RISK_ANNEX = true;

/**
 * Yönetilecek riskler — kaos senaryolarının hemen üstünde.
 *
 * İki liste bilinçli olarak ayrı başlıklarla ama aynı bölümde duruyor:
 * bunlar konseyin tartışırken tespit ettiği ve sahibi/azaltımı yazılmış
 * riskler; altındakiler ise plan taranırken karşılığı bulunamayan kriz
 * senaryoları. Farklı şeyler, aynı soruya hizmet ediyorlar.
 */
function risksSectionHtml(verdict: Verdict): string {
  const plan = coerceActionPlan(verdict.actionPlan);
  const risks = plan.risks ?? [];
  if (risks.length === 0) return "";
  return `<div class="risk-block">
    <h3>Konseyin tespit ettiği riskler · ${risks.length}</h3>
    <p class="risk-note">
      Bunlar kararın uygulanmasında yönetilmesi gereken bilinen risklerdir;
      her biri için azaltım yolu tanımlanmıştır. Karara itiraz değildir.
    </p>
    ${risksHtml(risks)}
  </div>`;
}

function actionPlanHtml(rawPlan: ActionPlanV2 | unknown): string {
  const plan = coerceActionPlan(rawPlan);
  const TIERS: { key: keyof ActionPlanV2; label: string; sub: string }[] = [
    { key: "immediate", label: "Acil", sub: "0-2 hafta · acil müdahale" },
    { key: "tactical", label: "Taktik", sub: "2-12 hafta · sprint icrası" },
    { key: "strategic", label: "Stratejik", sub: "3-12 ay · program kararları" },
    { key: "architectural", label: "Mimari", sub: "12-36 ay · platform/mimari" },
    { key: "governance", label: "Yönetişim", sub: "Sürekli · karar hakları & RACI" },
  ];

  const totalActions =
    plan.immediate.length +
    plan.tactical.length +
    plan.strategic.length +
    plan.architectural.length +
    plan.governance.length;

  // Rozet sözlüğü — `P0`/`S`/`M` kısaltmaları raporda açıklamasız
  // duruyordu ve okuyan yöneticiye hiçbir şey ifade etmiyordu.
  const badgeLegend = `<p class="ap-legend">
    <strong>Rozetler:</strong> P0 engelleyici · P1 olmalı · P2 iyi olur
    &nbsp;|&nbsp; Efor: XS ≤1 gün · S 1-3 gün · M 1-2 hafta ·
    L 2-6 hafta · XL &gt;6 hafta
  </p>`;

  const tierBlocks = TIERS.map((t) => {
    const items = plan[t.key] as ActionItem[];
    return `<div class="ap-tier">
      <h3>${escapeHtml(t.label)} <span class="ap-sub">${escapeHtml(t.sub)}</span> <span class="ap-count">${items.length}</span></h3>
      ${actionItemsHtml(items)}
    </div>`;
  }).join("");

  const annexes: string[] = [];
  // Risk register artık "Riskler ve Plan Boşlukları" bölümünde, kaos
  // senaryolarıyla birlikte tek yerde toplanıyor; burada tekrar
  // basılması aynı bilgiyi iki kez okutuyordu.
  if (!SUPPRESS_PLAN_RISK_ANNEX && (plan.risks?.length ?? 0) > 0) {
    annexes.push(`<div class="ap-annex">
      <h3>Risk Register · ${plan.risks?.length ?? 0}</h3>
      ${risksHtml(plan.risks ?? [])}
    </div>`);
  }
  if ((plan.kpis?.length ?? 0) > 0) {
    annexes.push(`<div class="ap-annex">
      <h3>Yürütme KPI'ları · ${plan.kpis?.length ?? 0}</h3>
      ${kpisHtml(plan.kpis ?? [])}
    </div>`);
  }
  if ((plan.criticalAssumptions?.length ?? 0) > 0) {
    annexes.push(`<div class="ap-annex">
      <h3>Kritik Varsayımlar · ${plan.criticalAssumptions?.length ?? 0}</h3>
      ${assumptionsHtml(plan.criticalAssumptions ?? [])}
    </div>`);
  }

  return `<div class="action-plan">
    <div class="ap-header">
      <span class="ap-summary"><strong>${totalActions}</strong> aksiyon · <strong>${plan.risks?.length ?? 0}</strong> risk · <strong>${plan.kpis?.length ?? 0}</strong> KPI · <strong>${plan.criticalAssumptions?.length ?? 0}</strong> varsayım</span>
      <span class="ap-framework">McKinsey 3-Horizons + SMART + RACI</span>
    </div>
    ${badgeLegend}
    ${tierBlocks}
    ${annexes.join("")}
  </div>`;
}

/**
 * Konsey Cognitive Renaissance — Bölüm D6.
 *
 * Section 12: Konsey Üyelerinin Bilişsel Profili. Per-persona kart;
 * Learning Coefficient, Interpretation Consistency, Neural Latency,
 * Peak Cognitive Load, Avg Amygdala Heat, Productivity Score.
 *
 * `brainProfiles` parametresi yoksa veya boşsa hiç render edilmez
 * (legacy verdict'ler için kırılmaz).
 */
function brainProfilesHtml(profiles?: BrainProfileForReport[]): string {
  if (!profiles || profiles.length === 0) return "";

  const cards = profiles
    .map((p) => {
      const learning = (p.learningCoefficient * 100).toFixed(1);
      const interp = (p.interpretationConsistency * 100).toFixed(0);
      const heat = (p.avgAmygdalaHeat * 100).toFixed(0);
      const cogLoad = p.peakCognitiveLoadPct.toFixed(0);
      const productivity = p.productivityScore.toFixed(0);
      const latency = p.avgNeuralLatencyMs.toFixed(0);
      // Renk kodları — operatör hızlı tarama için
      const productivityColor =
        p.productivityScore >= 75
          ? "var(--severity-minor)"
          : p.productivityScore >= 50
            ? "var(--severity-major)"
            : "var(--severity-blocker)";
      const cogColor =
        p.peakCognitiveLoadPct >= 90
          ? "var(--severity-blocker)"
          : p.peakCognitiveLoadPct >= 70
            ? "var(--severity-major)"
            : "var(--severity-minor)";
      return `
<div class="brain-card">
  <div class="brain-card-head">
    <strong>${escapeHtml(p.personaName)}</strong>
    <span class="brain-productivity" style="color:${productivityColor}">${productivity}/100</span>
  </div>
  <div class="brain-grid">
    <div class="brain-cell">
      <span class="brain-key">Öğrenme Katsayısı</span>
      <span class="brain-val">+${learning}%</span>
    </div>
    <div class="brain-cell">
      <span class="brain-key">Yorumlama Tutarlılığı</span>
      <span class="brain-val">${interp}%</span>
    </div>
    <div class="brain-cell">
      <span class="brain-key">Nöral Gecikme (ort.)</span>
      <span class="brain-val">${latency} ms</span>
    </div>
    <div class="brain-cell">
      <span class="brain-key">Zirve Bilişsel Yük</span>
      <span class="brain-val" style="color:${cogColor}">${cogLoad}%</span>
    </div>
    <div class="brain-cell">
      <span class="brain-key">Ort. Amygdala Heat</span>
      <span class="brain-val">${heat}%</span>
    </div>
    <div class="brain-cell">
      <span class="brain-key">Üretkenlik (composite)</span>
      <span class="brain-val" style="color:${productivityColor}">${productivity}/100</span>
    </div>
  </div>
</div>`;
    })
    .join("");

  // Başlık burada basılmaz: `buildVerdictReportHtml` içindeki `section()`
  // numarayı sıraya göre verir (elle yazılan "11" bölüm eklendiğinde
  // sessizce yanlışa düşüyordu).
  return `
    <p class="lead" style="font-size:12px;color:var(--fg-muted)">
      Her konsey üyesi tartışma boyunca biyolojik beyin metaforu altında
      analiz edilmiştir. <strong>Hipokampus</strong> (kısa süreli RAG/vektör buffer),
      <strong>Serebral Korteks</strong> (uzun süreli bilgi tabanı),
      <strong>Prefrontal Korteks</strong> (karar verme + planlama),
      <strong>Amigdala</strong> (duygusal ton + tehdit algısı),
      <strong>Serebellum</strong> (prosedürel bellek),
      <strong>Parietal/Temporal/Oksipital Loblar</strong> (odak / dilsel anlama / görsel).
      Aşağıdaki kart sistemi, her üye için tartışma sonu metriklerin özetini sunar.
    </p>
    <div class="brain-cards">
      ${cards}
    </div>
  `;
}

function transcriptHtml(personas: Persona[], turns: Turn[]): string {
  if (turns.length === 0) {
    return `<p class="empty">Tartışma transkripti boş.</p>`;
  }
  const byId = new Map(personas.map((p) => [p.id, p]));
  return turns
    .filter((t) => turnText(t).length > 0)
    .map((t) => {
      const p = byId.get(t.speakerId);
      const name = p ? p.displayName : t.speakerId;
      const role = p ? p.expertise || p.role : "";
      const text = turnText(t);
      const outcomeLabel =
        t.outcome === "forfeited"
          ? `<span class="badge severity-major" style="margin-left:6px">PAS</span>`
          : "";
      const timing =
        t.startedAt && t.endedAt
          ? ` · ${Math.round((t.endedAt - t.startedAt) / 1000)}sn`
          : "";

      const critiqueBlock =
        t.critique
          ? `<div class="turn-critique">
               <span class="critique-label">KRİTİK</span>
               <span class="badge severity-${t.critique.severity}">${SEVERITY_LABELS[t.critique.severity] ?? t.critique.severity}</span>
               ${escapeHtml(t.critique.flaw)}
             </div>`
          : "";

      const thinkingBlock =
        t.thinking?.summary
          ? `<details class="turn-thinking">
               <summary>Düşünme süreci (${t.thinking.chars.toLocaleString("tr-TR")} karakter)</summary>
               <p>${escapeHtml(t.thinking.summary)}</p>
             </details>`
          : "";

      const researchBlock =
        t.research && t.research.length > 0
          ? `<div class="turn-research">
               <span class="research-label">ARAŞTIRMA</span>
               ${t.research
                 .map(
                   (r) =>
                     `<span class="research-query">"${escapeHtml(truncate(r.query, 80))}"</span>
                      <span class="muted">${r.sources.length} kaynak · ${r.elapsedMs}ms${r.cacheHit ? " · önbellek" : ""}</span>`,
                 )
                 .join("<br/>")}
             </div>`
          : "";

      const ragBlock =
        t.ragBatches && t.ragBatches.length > 0
          ? `<div class="turn-research">
               <span class="research-label">BELGE</span>
               ${t.ragBatches
                 .map(
                   (b) =>
                     `<span class="muted">${b.citations.length} parça — "${escapeHtml(truncate(b.query, 60))}"</span>`,
                 )
                 .join("<br/>")}
             </div>`
          : "";

      return `
<article class="turn">
  <header>
    <span class="turn-meta">Tur ${t.roundIndex + 1} · ${escapeHtml(name)}${role ? ` <span class="role">— ${escapeHtml(role)}</span>` : ""}${timing}${outcomeLabel}</span>
  </header>
  ${researchBlock}
  ${ragBlock}
  <p>${escapeHtml(text)}</p>
  ${critiqueBlock}
  ${thinkingBlock}
</article>`;
    })
    .join("");
}

function turnText(t: Turn): string {
  const finalText = t.finalText?.trim();
  if (finalText) return finalText;
  return (t.tokens ?? []).join("").trim();
}

function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1).trimEnd() + "…";
}

function personasHtml(personas: Persona[], metrics?: DebateAggregateMetrics): string {
  const bySpeaker = new Map(metrics?.bySpeaker.map((s) => [s.speakerId, s]) ?? []);
  return personas
    .map((p) => {
      const sources = p.asymmetry?.sources ?? [];
      const blindSpots = p.asymmetry?.blindSpots ?? [];
      const ceiling = p.asymmetry?.confidenceCeiling;
      const speaker = bySpeaker.get(p.id);
      const sysSummary = p.systemInstruction
        ? truncate(p.systemInstruction.replace(/\s+/g, " "), 320)
        : "";
      const sharePct = speaker ? `${Math.round(speaker.share * 100)}%` : "—";
      const turnsText = speaker ? `${speaker.turns} söz` : "söz yok";
      const sourcesHtml =
        sources.length > 0
          ? `<div class="bio-row"><span class="bio-k">Bilgi Kaynakları</span><span class="bio-v">${sources
              .slice(0, 4)
              .map((s) => escapeHtml(s))
              .join(" · ")}</span></div>`
          : "";
      const blindHtml =
        blindSpots.length > 0
          ? `<div class="bio-row"><span class="bio-k">Kör Noktalar</span><span class="bio-v">${blindSpots
              .slice(0, 3)
              .map((s) => escapeHtml(s))
              .join(" · ")}</span></div>`
          : "";
      const sysHtml = sysSummary
        ? `<div class="bio-row bio-sys"><span class="bio-k">Sistem Yönergesi</span><span class="bio-v">${escapeHtml(sysSummary)}</span></div>`
        : "";
      const confidenceHtml =
        typeof ceiling === "number"
          ? `<div class="bio-row"><span class="bio-k">Güven Tavanı</span><span class="bio-v">${Math.round(ceiling * 100)}%</span></div>`
          : "";
      return `
<li class="persona">
  <div class="persona-head">
    <div class="persona-name">${escapeHtml(p.displayName)}</div>
    <span class="persona-ceiling" title="Konuşma payı">${sharePct}</span>
  </div>
  <div class="persona-role">${escapeHtml(p.expertise || p.role)}</div>
  <div class="bio-row"><span class="bio-k">Katılım</span><span class="bio-v">${escapeHtml(turnsText)} · ${escapeHtml(sharePct)} konuşma payı</span></div>
  ${confidenceHtml}
  ${sysHtml}
  ${sourcesHtml}
  ${blindHtml}
</li>`;
    })
    .join("");
}

function metricsGridHtml(
  topologyLabel: string,
  personaCount: number,
  turns: Turn[],
  startedAt: number | null,
  generatedAt: number,
  metrics?: DebateAggregateMetrics,
  sources?: FlatResearchSource[],
  ragCitations?: FlatRagCitation[],
): string {
  const totalChars = metrics?.totalCharacters ?? 0;
  const totalSources = metrics?.totalSources ?? sources?.length ?? 0;
  const totalRag = metrics?.totalRagCitations ?? ragCitations?.length ?? 0;
  const totalRounds = metrics?.totalRounds ?? turns.length;
  const totalTurns = metrics?.totalTurns ?? turns.length;
  return `
<div class="meta-grid">
  <div class="meta-card"><div class="k">Topoloji</div><div class="v">${escapeHtml(topologyLabel)}</div></div>
  <div class="meta-card"><div class="k">Üye Sayısı</div><div class="v">${personaCount}</div></div>
  <div class="meta-card"><div class="k">Tur Sayısı</div><div class="v">${totalRounds}</div></div>
  <div class="meta-card"><div class="k">Tamamlanan Söz</div><div class="v">${totalTurns}</div></div>
  <div class="meta-card"><div class="k">Metin Uzunluğu</div><div class="v">${totalChars.toLocaleString("tr-TR")} karakter</div></div>
  <div class="meta-card"><div class="k">Web Kaynağı</div><div class="v">${totalSources}</div></div>
  <div class="meta-card"><div class="k">Belge Atıfı</div><div class="v">${totalRag}</div></div>
  <div class="meta-card"><div class="k">Süre</div><div class="v">${durationLabel(startedAt, generatedAt)}</div></div>
  <div class="meta-card"><div class="k">Karar Tarihi</div><div class="v">${escapeHtml(fmtDate(generatedAt))}</div></div>
</div>`;
}

function speakerShareHtml(metrics?: DebateAggregateMetrics): string {
  if (!metrics || metrics.bySpeaker.length === 0) {
    return `<p class="empty">Konuşma dağılımı için tamamlanmış konuşma metni bulunamadı.</p>`;
  }
  return `
<table class="dissent">
  <thead>
    <tr><th>Üye</th><th>Tur</th><th>Karakter</th><th>Pay</th></tr>
  </thead>
  <tbody>
    ${metrics.bySpeaker
      .map(
        (s) => `<tr>
          <td><span class="dot" style="background:${escapeHtml(s.themeColor ?? "#7c5cff")}"></span>${escapeHtml(s.displayName)}${
          s.expertise ? ` <span class="muted">— ${escapeHtml(s.expertise)}</span>` : ""
        }</td>
          <td>${s.turns}</td>
          <td>${s.characters.toLocaleString("tr-TR")}</td>
          <td>${Math.round(s.share * 100)}%</td>
        </tr>`,
      )
      .join("")}
  </tbody>
</table>`;
}

function sourcesBibliographyHtml(sources?: FlatResearchSource[]): string {
  if (!sources || sources.length === 0) {
    return `<p class="empty">Bu tartışmada canlı kaynak çekilmedi.</p>`;
  }
  return `
<ol class="bibliography">
  ${sources
    .map(
      (r, i) => `<li>
      <span class="bib-num">${i + 1}.</span>
      <div class="bib-body">
        <a href="${escapeHtml(r.source.url)}" target="_blank" rel="noopener">${escapeHtml(r.source.title || r.source.name)}</a>
        <div class="bib-meta">
          <span class="badge bib-domain">${escapeHtml(r.source.name)}</span>
          <span class="muted">— ${escapeHtml(r.speakerName)}</span>
          ${r.source.published ? ` · <span class="muted">${escapeHtml(r.source.published)}</span>` : ""}
        </div>
        ${r.source.summary ? `<div class="bib-summary">${escapeHtml(truncate(r.source.summary, 280))}</div>` : ""}
      </div>
    </li>`,
    )
    .join("")}
</ol>`;
}

function commentaryHtml(verdict: Verdict): string {
  const analysis = verdict.commentaryAnalysis;
  const critique = verdict.commentaryCritique;
  if (!analysis && !critique) {
    return `<p class="empty">Bu karar için editoryal yorum henüz üretilmedi.</p>`;
  }
  let html = "";
  if (analysis) {
    html += `
<div class="commentary-block commentary-analysis">
  <div class="commentary-header">
    <span class="commentary-icon">📊</span>
    <span class="commentary-title">Yapıcı Analiz</span>
  </div>
  <p>${escapeHtml(analysis)}</p>
</div>`;
  }
  if (critique) {
    html += `
<div class="commentary-block commentary-critique">
  <div class="commentary-header">
    <span class="commentary-icon">⚡</span>
    <span class="commentary-title">Eleştirel Perspektif</span>
  </div>
  <p>${escapeHtml(critique)}</p>
</div>`;
  }
  return html;
}

function simulationHtml(verdict: Verdict): string {
  const score = verdict.simulationScore;
  const outcomes = verdict.simulationOutcomes ?? [];
  if (score == null || outcomes.length === 0) {
    return `<p class="empty">Bu karar için simülasyon çalışması yapılmadı veya sonuçlar mevcut değil.</p>`;
  }
  const survived = outcomes.filter((o) => o.survived).length;
  const total = outcomes.length;
  const pct = Math.round(score * 100);
  // Etiketler PLANI niteler, kararı değil. "Kırılgan plan" ifadesi
  // yöneticiye konseyin yatırımı yanlış bulduğu izlenimi veriyordu;
  // ölçülen şey planın kriz senaryolarına karşılık verip vermediği.
  const robustness =
    score >= 0.83
      ? "Plan kapsamlı"
      : score >= 0.5
        ? "Planda kısmi boşluk"
        : "Planda boşluk var";
  const robustnessColor =
    score >= 0.83
      ? "var(--severity-minor)"
      : "var(--severity-major)";
  const sorted = [...outcomes].sort((a, b) =>
    a.survived === b.survived ? 0 : a.survived ? 1 : -1,
  );
  return `
<div class="sim-methodology">
  <h3>Metodoloji</h3>
  <p>
    Konsey kararı kesinleştirildikten sonra, eylem planının dayanıklılığını ölçmek için otomatik bir
    <strong>kaos simülasyonu</strong> uygulanmıştır. Bu simülasyonda bağımsız bir yapay zeka modeli,
    kararın eylem planını ${total} adet önceden tanımlanmış gerçekçi kriz senaryosuna karşı test etmiştir.
  </p>
  <p>
    Her senaryo için model, eylem planında o krize karşılık verecek bir madde bulunup bulunmadığına
    bakmış ve gerekçesini yazmıştır. <strong>Bu test kararın doğruluğunu ölçmez</strong>; kararın
    uygulama planının ne kadar eksiksiz olduğunu ölçer. Karşılıksız kalan bir senaryo, yatırımın
    yanlış olduğu anlamına gelmez — plana bir önlem maddesi eklenmesi gerektiği anlamına gelir.
  </p>
  <div class="sim-thresholds">
    <span class="sim-th"><span class="dot" style="background:var(--severity-minor)"></span> ≥%83 → Plan kapsamlı</span>
    <span class="sim-th"><span class="dot" style="background:var(--severity-major)"></span> %50–82 → Kısmi boşluk</span>
    <span class="sim-th"><span class="dot" style="background:var(--severity-major)"></span> &lt;%50 → Planda boşluk var</span>
  </div>
</div>
<div class="sim-summary">
  <div class="sim-score" style="border-color: ${robustnessColor}">
    <div class="sim-pct" style="color: ${robustnessColor}">${pct}%</div>
    <div class="sim-frac">${survived}/${total} senaryo</div>
    <div class="sim-label" style="color: ${robustnessColor}">${robustness}</div>
  </div>
  <div class="sim-desc">
    <strong>Sonuç:</strong> Eylem planı ${total} kriz senaryosuna karşı tarandı;
    ${survived} senaryoda planda karşılık veren bir madde bulundu, ${total - survived}
    senaryoda bulunmadı (<strong>%${pct}</strong> kapsama · <strong>"${robustness}"</strong>).
    ${survived < total
      ? `Karşılıksız kalan ${total - survived} senaryo aşağıda listelenmiştir. Bunlar kararın
         gerekçesine itiraz değil, uygulama planına eklenmesi önerilen önlem başlıklarıdır.`
      : "Planda tüm senaryolar için karşılık bulunmuştur."}
  </div>
</div>
<table class="dissent sim-table">
  <thead>
    <tr><th style="width:28px">Durum</th><th>Kriz Senaryosu</th><th>Planda karşılık</th></tr>
  </thead>
  <tbody>
    ${sorted
      .map(
        (o) => `<tr>
          <td style="text-align:center"><span class="sim-icon ${o.survived ? "sim-pass" : "sim-gap"}" title="${o.survived ? "Planda karşılık var" : "Planda karşılık yok — önlem maddesi önerilir"}">${o.survived ? "✓" : "!"}</span></td>
          <td><strong>${escapeHtml(o.scenario)}</strong></td>
          <td>${escapeHtml(o.rationale)}</td>
        </tr>`,
      )
      .join("")}
  </tbody>
</table>`;
}

function ragBibliographyHtml(citations?: FlatRagCitation[]): string {
  if (!citations || citations.length === 0) {
    return `<p class="empty">Yüklenen belgelerden bu tartışmada atıf yapılmadı.</p>`;
  }
  return `
<ol class="bibliography rag-bib">
  ${citations
    .map(
      (c, i) => `<li>
      <span class="bib-num">${i + 1}.</span>
      <div class="bib-body">
        <div class="rag-title">${escapeHtml(c.citation.filename)}</div>
        <div class="bib-meta">
          <span class="badge bib-rag">${c.citation.scope === "private" ? "ÖZEL" : "ORTAK"}</span>
          <span class="muted">parça #${c.citation.chunkIdx}</span>
          <span class="muted">· benzerlik ${(c.citation.score * 100).toFixed(0)}%</span>
          <span class="muted">— ${escapeHtml(c.speakerName)}</span>
          ${c.reuseCount > 1 ? ` · <span class="muted">${c.reuseCount} üye okudu</span>` : ""}
        </div>
        ${c.citation.excerpt ? `<div class="bib-summary">"${escapeHtml(truncate(c.citation.excerpt, 320))}"</div>` : ""}
      </div>
    </li>`,
    )
    .join("")}
</ol>`;
}

/* ────────────────────────────────────────────────────────────────────
   Görev 18-24 + Karar Paketi yüzeyleri.

   Bu bloktaki her şey web panelinde uzun süredir vardı ama rapora hiç
   taşınmamıştı: PDF ayrı bir HTML şablonu olduğu için ekrana eklenen
   bölümler otomatik olarak çıktıya girmiyor. Operatörün "PDF'te
   eksikler var" gözlemi tam olarak bu drift'ti.

   Backend sözleşmesi hâlâ evrildiği için payload'lar gevşek tiplidir;
   her alan savunmacı okunur ve veri yoksa bölüm HİÇ basılmaz (boş
   iskelet, olmayan veriyi varmış gibi göstermekten kötüdür).
   ──────────────────────────────────────────────────────────────────── */

type Loose = Record<string, unknown>;

const asRecord = (v: unknown): Loose | null =>
  v && typeof v === "object" && !Array.isArray(v) ? (v as Loose) : null;

const asArray = (v: unknown): Loose[] =>
  Array.isArray(v)
    ? v.filter((x): x is Loose => !!x && typeof x === "object")
    : [];

const asStrings = (v: unknown): string[] =>
  Array.isArray(v)
    ? v.filter((x): x is string => typeof x === "string" && x.trim() !== "")
    : [];

const lstr = (v: unknown, fallback = ""): string =>
  typeof v === "string" && v.trim() ? v.trim() : fallback;

const lnum = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;

const pctLabel = (v: number | null): string =>
  v === null ? "—" : `${Math.round(v * 100)}%`;

function chipHtml(text: string, tone?: "ok" | "warn" | "bad"): string {
  const cls = tone ? ` chip-${tone}` : "";
  return `<span class="kp-chip${cls}">${escapeHtml(text)}</span>`;
}

/** BLUF — kararın tek cümlelik hâli, raporun en üstünde. */
function blufHtml(verdict: Verdict): string {
  const b = verdict.bluf;
  const recommendation = lstr(b?.recommendation);
  if (!b || !recommendation) return "";

  const reasons = asStrings(b.reasons).slice(0, 3);
  const needToKnow = asStrings(b.needToKnow);
  const reversal = asStrings(b.reversalConditions);
  const confidence = lnum(b.confidence);
  const mainRisk = lstr(b.mainRisk);

  const badges: string[] = [];
  if (mainRisk) badges.push(chipHtml(`Ana risk: ${mainRisk}`, "bad"));
  if (confidence !== null)
    badges.push(chipHtml(`Güven: ${Math.round(confidence * 100)}%`));
  if (needToKnow[0]) badges.push(chipHtml(`Emin olmak için: ${needToKnow[0]}`));

  return `
<div class="bluf">
  <div class="bluf-eyebrow">BLUF — Sonuç Önce</div>
  <p class="bluf-rec">${escapeHtml(recommendation)}</p>
  ${
    reasons.length > 0
      ? `<ul class="bluf-reasons">${reasons
          .map((r) => `<li>${escapeHtml(r)}</li>`)
          .join("")}</ul>`
      : ""
  }
  ${badges.length > 0 ? `<div class="bluf-badges">${badges.join("")}</div>` : ""}
  ${
    reversal.length > 0
      ? `<p class="bluf-reversal"><strong>Karar tersine döner eğer:</strong> ${escapeHtml(
          reversal.join(" · "),
        )}</p>`
      : ""
  }
</div>`;
}

/**
 * Karar güveni + kanıt kalitesi + insan onay kapısı.
 *
 * Üçü de web'de başlık şeridinde duruyor. Rapora bakan yönetici,
 * kararın onay bekleyip beklemediğini görmeden imzalayabiliyordu.
 */
function decisionStatusHtml(verdict: Verdict): string {
  const rows: string[] = [];

  const cm = asRecord(verdict.confidenceMetrics);
  const overall = lnum(cm?.overall);
  if (overall !== null) {
    const tone = overall >= 0.75 ? "ok" : overall >= 0.5 ? "warn" : "bad";
    const parts = Object.entries(cm ?? {})
      .filter(([k, v]) => typeof v === "number" && k !== "overall")
      .map(([k, v]) => `${k}: ${(v as number).toFixed(2)}`)
      .join(" · ");
    rows.push(`<li>
      ${chipHtml(
        `Karar güveni: ${overall >= 0.75 ? "Yüksek" : overall >= 0.5 ? "Orta" : "Düşük"} (${Math.round(
          overall * 100,
        )}%)`,
        tone,
      )}
      ${parts ? `<span class="ds-detail">Bileşenler — ${escapeHtml(parts)}</span>` : ""}
    </li>`);
  }

  if (verdict.evidenceQuality === "degraded") {
    rows.push(`<li>
      ${chipHtml("Kanıt kalitesi: düşürülmüş", "warn")}
      <span class="ds-detail">Bu oturumda en az bir bileşen (araştırma, belge
      erişimi veya karar sentezi) yedek yola düştü. Kanıt tabanı normalden
      dardır; kritik rakamları bağımsız doğrulayın.</span>
    </li>`);
  }

  const vs = verdict.verdictStatus;
  if (vs) {
    const state = lstr(vs.state);
    const STATE_LABEL: Record<string, string> = {
      answered: "Karar verildi",
      abstained: "Karardan kaçınıldı",
      degraded: "Düşürülmüş koşulda karar",
      review_required: "İnsan onayı bekliyor",
    };
    if (state) {
      const tone =
        state === "answered" ? "ok" : state === "review_required" ? "bad" : "warn";
      rows.push(
        `<li>${chipHtml(`Durum: ${STATE_LABEL[state] ?? state}`, tone)}</li>`,
      );
    }
    const reasons = asStrings(vs.reasons);
    if (reasons.length > 0) {
      rows.push(
        `<li><span class="ds-detail"><strong>Gerekçe:</strong> ${escapeHtml(
          reasons.join(" · "),
        )}</span></li>`,
      );
    }
    const hr = vs.humanReview;
    if (hr && lstr(hr.decision)) {
      const DEC: Record<string, string> = {
        approve: "Onaylandı",
        conditional: "Şartlı onay",
        reject: "Reddedildi",
      };
      const dec = lstr(hr.decision);
      const conds = asStrings(hr.conditions);
      rows.push(`<li>
        ${chipHtml(
          `İnsan incelemesi: ${DEC[dec] ?? dec}`,
          dec === "approve" ? "ok" : dec === "reject" ? "bad" : "warn",
        )}
        ${hr.note ? `<span class="ds-detail">${escapeHtml(String(hr.note))}</span>` : ""}
        ${
          conds.length > 0
            ? `<span class="ds-detail"><strong>Koşullar:</strong> ${escapeHtml(
                conds.join(" · "),
              )}</span>`
            : ""
        }
      </li>`);
    }
  }

  if (rows.length === 0) return "";
  return `<ul class="decision-status">${rows.join("")}</ul>`;
}

/** Gerçekten atıf yapılan kaynaklar — taranan tüm kaynaklardan farklı. */
function evidenceBibliographyHtml(verdict: Verdict): string {
  const bib = verdict.evidenceBibliography ?? [];
  if (bib.length === 0) return "";
  return `
<div class="cited-block">
  <h3>Gerçekten atıf yapılan kaynaklar · ${bib.length}</h3>
  <p class="risk-note">
    Aşağıdaki kaynaklar yalnızca taranmadı; en az bir konuşmacı final
    metninde adıyla veya alan adıyla açıkça bunlara dayandı. Liste kod
    tarafından hesaplanır, modele yazdırılmaz.
  </p>
  <table class="kpi-table">
    <thead><tr><th>Kaynak</th><th>Tur</th><th>Konuşmacı</th></tr></thead>
    <tbody>${bib
      .map(
        (e) => `<tr>
        <td>${
          e.url
            ? `<a href="${escapeHtml(e.url)}" target="_blank" rel="noopener">${escapeHtml(e.name)}</a>`
            : escapeHtml(e.name)
        }</td>
        <td>${e.citedByTurnCount}</td>
        <td>${e.citedBySpeakerIds.length}</td>
      </tr>`,
      )
      .join("")}</tbody>
  </table>
</div>`;
}

/** Dayanıklılık kapanışı — ilk plan → güçlendirilmiş plan. */
function resilienceHtml(verdict: Verdict): string {
  const status = lstr(verdict.resilienceStatus);
  const baseline = lnum(verdict.baselineSimulationScore ?? verdict.simulationScore ?? null);
  const hardened = lnum(verdict.hardenedSimulationScore ?? null);
  if (!status && baseline === null) return "";

  const LABEL: Record<string, string> = {
    completed: "Güçlendirme kabul edildi",
    regression_rejected: "Gerileme reddedildi — ilk plan korundu",
    baseline_only: "Yalnız ilk stres testi",
    no_gaps: "Boşluk bulunmadı",
    skipped: "Çalıştırılmadı",
  };
  const runs = asArray(verdict.simulationRuns);
  const baselineRun = runs.find((r) => lstr(r.phase) === "baseline");
  const gaps = asArray(baselineRun?.gaps);
  const controls = asArray(asRecord(verdict.hardenedActionPlan)?.resilienceControls);
  const improved = baseline !== null && hardened !== null && hardened > baseline;

  return `
<div class="resilience">
  <h3>Dayanıklılık kapanışı${status ? ` · ${escapeHtml(LABEL[status] ?? status)}` : ""}</h3>
  <div class="res-scores">
    <div class="res-box"><span class="res-cap">İlk plan</span><span class="res-val">${pctLabel(baseline)}</span></div>
    <span class="res-arrow">→</span>
    <div class="res-box${improved ? " res-better" : ""}"><span class="res-cap">Güçlendirilmiş plan</span><span class="res-val">${pctLabel(hardened)}</span></div>
    ${controls.length > 0 ? chipHtml(`${controls.length} kontrol eklendi`, "ok") : ""}
  </div>
  ${
    gaps.length > 0
      ? `<table class="risk-table">
          <thead><tr><th>Eksik kontrol</th><th>Şiddet</th><th>Düşen senaryo</th></tr></thead>
          <tbody>${gaps
            .map(
              (g) => `<tr>
              <td>${escapeHtml(lstr(g.missing_control, lstr(g.category, "—")))}</td>
              <td><span class="risk-level risk-${escapeHtml(lstr(g.severity, "medium"))}">${escapeHtml(lstr(g.severity, "medium"))}</span></td>
              <td>${escapeHtml(asStrings(g.scenarios).join(" · ") || "—")}</td>
            </tr>`,
            )
            .join("")}</tbody>
        </table>`
      : ""
  }
</div>`;
}

/** Board brief — yönetim kuruluna giden özet paket. */
function boardBriefHtml(verdict: Verdict): string {
  const brief = asRecord(verdict.boardBrief);
  if (!brief) return "";

  const recommendation = lstr(brief.recommendation);
  const ask = lstr(brief.decisionAsk);
  const moves = asArray(brief.headlineMoves);
  const gating = asArray(brief.gatingDecisions);
  const risks = asArray(brief.topRisks);
  const measurement = asArray(brief.measurement);
  const dissent = asArray(brief.openDissent);
  const assumptions = asStrings(brief.criticalAssumptions);
  const evidence = asRecord(brief.evidenceBase);
  const roles = asStrings(brief.accountableRoles);

  const blocks: string[] = [];

  if (recommendation) {
    blocks.push(`<div class="bb-rec">
      <div class="bb-cap">Karar önerisi</div>
      <p>${escapeHtml(recommendation)}</p>
      ${ask ? `<p class="bb-ask">${escapeHtml(ask)}</p>` : ""}
    </div>`);
  }

  if (moves.length > 0) {
    blocks.push(`<h4>Öncelikli hamleler · ${moves.length}</h4>
    <ol class="action-items">${moves
      .map((m) => {
        const meta = [
          lstr(m.accountable) || lstr(m.owner),
          lstr(m.deadlineWindow),
          lstr(m.successMetric),
        ].filter(Boolean);
        return `<li>
          <div class="ai-head">
            <span class="ai-pri ai-pri-${escapeHtml(lstr(m.priority, "P1"))}">${escapeHtml(lstr(m.priority, "P1"))}</span>
            <span class="ai-eff">${escapeHtml(lstr(m.horizonLabel, lstr(m.horizon, "—")))}</span>
            <span class="ai-title">${escapeHtml(lstr(m.title))}</span>
          </div>
          ${meta.length ? `<div class="ai-meta">${escapeHtml(meta.join(" · "))}</div>` : ""}
        </li>`;
      })
      .join("")}</ol>`);
  }

  if (gating.length > 0) {
    blocks.push(`<h4>Kilitleyici kapılar · ${gating.length}</h4>
    <p class="risk-note">Bunlar açılmadan program ilerlemez.</p>
    <ul class="assumptions">${gating
      .map(
        (g) =>
          `<li><strong>${escapeHtml(lstr(g.title))}</strong>${
            lstr(g.reason) ? ` — ${escapeHtml(lstr(g.reason))}` : ""
          }${lstr(g.deadlineWindow) ? ` · ${escapeHtml(lstr(g.deadlineWindow))}` : ""}</li>`,
      )
      .join("")}</ul>`);
  }

  if (risks.length > 0) {
    blocks.push(`<h4>Risk zirvesi · ${risks.length}</h4>
    <table class="risk-table">
      <thead><tr><th>Risk</th><th>Olasılık</th><th>Etki</th><th>Önlem</th><th>Sahip</th></tr></thead>
      <tbody>${risks
        .map(
          (r) => `<tr>
          <td>${escapeHtml(lstr(r.risk))}</td>
          <td><span class="risk-level risk-${escapeHtml(lstr(r.likelihood, "medium"))}">${escapeHtml(lstr(r.likelihood, "medium"))}</span></td>
          <td><span class="risk-level risk-${escapeHtml(lstr(r.impact, "medium"))}">${escapeHtml(lstr(r.impact, "medium"))}</span></td>
          <td>${escapeHtml(lstr(r.mitigation, "—"))}</td>
          <td>${escapeHtml(lstr(r.owner, "—"))}</td>
        </tr>`,
        )
        .join("")}</tbody>
    </table>`);
  }

  if (measurement.length > 0) {
    blocks.push(`<h4>Ölçüm çerçevesi</h4>
    <table class="kpi-table">
      <thead><tr><th>Metrik</th><th>Hedef</th><th>Kadans</th></tr></thead>
      <tbody>${measurement
        .map(
          (k) => `<tr>
          <td>${escapeHtml(lstr(k.metric))}</td>
          <td>${escapeHtml(lstr(k.target, "—"))}</td>
          <td>${escapeHtml(lstr(k.cadence, "—"))}</td>
        </tr>`,
        )
        .join("")}</tbody>
    </table>`);
  }

  if (dissent.length > 0) {
    blocks.push(`<h4>Açık muhalefet · ${dissent.length}</h4>
    <p class="risk-note">Karar sahibinin görmesi gereken çözümsüz görüşler.</p>
    <table class="dissent">
      <thead><tr><th>Üye</th><th>Şerh</th><th>Şiddet</th></tr></thead>
      <tbody>${dissent
        .map(
          (d) => `<tr>
          <td>${escapeHtml(lstr(d.who, "—"))}</td>
          <td>${escapeHtml(lstr(d.concern))}</td>
          <td>${escapeHtml(lstr(d.severityLabel, lstr(d.severity, "minor")))}</td>
        </tr>`,
        )
        .join("")}</tbody>
    </table>`);
  }

  if (assumptions.length > 0) {
    blocks.push(`<h4>Kritik varsayımlar</h4>
    <p class="risk-note">Bunlar değişirse plan revize edilmelidir.</p>
    <ul class="assumptions">${assumptions
      .map((a) => `<li>${escapeHtml(a)}</li>`)
      .join("")}</ul>`);
  }

  if (evidence) {
    const cells: [string, number | null][] = [
      ["Atıf yapılan kaynak", lnum(evidence.citedSourceCount)],
      ["Toplanan kaynak", lnum(evidence.researchSourceCount)],
      ["Kanıt çelişkisi", lnum(evidence.conflictCount)],
      ["Kapsam skoru", lnum(evidence.coverageScore)],
    ];
    const present = cells.filter(([, v]) => v !== null);
    if (present.length > 0) {
      blocks.push(`<h4>Kanıt tabanı</h4>
      <table class="kpi-table">
        <tbody>${present
          .map(
            ([label, v]) =>
              `<tr><td>${escapeHtml(label)}</td><td>${String(v)}</td></tr>`,
          )
          .join("")}</tbody>
      </table>`);
    }
  }

  if (roles.length > 0) {
    blocks.push(
      `<h4>Sorumlu roller</h4><div class="bb-roles">${roles
        .map((r) => chipHtml(r))
        .join("")}</div>`,
    );
  }

  return blocks.length > 0 ? `<div class="board-brief">${blocks.join("")}</div>` : "";
}

/** Karar hafızası — inceleme takvimi ve yeniden değerlendirme sinyalleri. */
function decisionMemoryHtml(verdict: Verdict): string {
  const memory = asRecord(verdict.decisionMemory);
  if (!memory) return "";
  const calendar = asArray(memory.reviewCalendar);
  const triggers = asArray(memory.reviewTriggers);
  if (calendar.length === 0 && triggers.length === 0) return "";
  const next = lnum(memory.nextReviewWeekOffset);

  const TRIGGER_LABEL: Record<string, string> = {
    assumption_broken: "Varsayım kırıldı",
    stress_scenario: "Stres senaryosu",
    unresolved_dissent: "Çözümsüz muhalefet",
  };

  return `
<div class="decision-memory">
  <h3>Karar hafızası${next !== null ? ` · sonraki kontrol T+${next} hafta` : ""}</h3>
  ${
    calendar.length > 0
      ? `<table class="kpi-table">
          <thead><tr><th>Dönem</th><th>Gündem</th><th>Madde</th></tr></thead>
          <tbody>${calendar
            .map(
              (c) => `<tr>
              <td>${escapeHtml(lstr(c.label, "—"))}</td>
              <td>${escapeHtml(asStrings(c.itemsDue).join(" · ") || "—")}</td>
              <td>${lnum(c.itemCount) ?? 0}</td>
            </tr>`,
            )
            .join("")}</tbody>
        </table>`
      : ""
  }
  ${
    triggers.length > 0
      ? `<h4>Yeniden değerlendirme tetikleyicileri · ${triggers.length}</h4>
        <ul class="assumptions">${triggers
          .map(
            (t) =>
              `<li><strong>${escapeHtml(
                TRIGGER_LABEL[lstr(t.kind)] ?? lstr(t.kind, "sinyal"),
              )}</strong> — ${escapeHtml(lstr(t.signal))}${
                lstr(t.action) ? ` → ${escapeHtml(lstr(t.action))}` : ""
              }</li>`,
          )
          .join("")}</ul>`
      : ""
  }
</div>`;
}

/** Kapsam matrisi + tutarlılık bayrakları. */
function coverageHtml(verdict: Verdict): string {
  const matrix = coerceCoverageMatrix(verdict.coverage_matrix);
  const flags = coerceConsistencyFlags(verdict.consistency_flags);
  if (!matrix && flags.length === 0) return "";

  const STATUS_LABEL: Record<string, string> = {
    covered: "karşılandı",
    partial: "kısmen",
    uncovered: "karşılanmadı",
  };

  const matrixHtml = matrix
    ? `<h4>Kapsam matrisi · ${Math.round(matrix.score * 100)}%${
        matrix.deliverableType ? ` · ${escapeHtml(matrix.deliverableType)}` : ""
      }</h4>
      <table class="kpi-table">
        <thead><tr><th>Gereksinim</th><th>Tür</th><th>Durum</th><th>Kanıt turu</th></tr></thead>
        <tbody>${matrix.requirements
          .map(
            (r) => `<tr>
            <td>${escapeHtml(r.text)}</td>
            <td>${escapeHtml(r.kind || "—")}</td>
            <td><span class="risk-level risk-${
              r.status === "covered" ? "low" : r.status === "partial" ? "medium" : "high"
            }">${escapeHtml(STATUS_LABEL[r.status] ?? r.status)}</span></td>
            <td>${r.evidenceTurnIds.length}</td>
          </tr>`,
          )
          .join("")}</tbody>
      </table>`
    : "";

  const flagsHtml =
    flags.length > 0
      ? `<h4>Tutarlılık bayrakları · ${flags.length}</h4>
        <ul class="assumptions">${flags
          .map((f) => {
            const kind = lstr(f.kind, lstr(f.type, "bulgu"));
            const detail = lstr(f.detail, lstr(f.message, lstr(f.summary)));
            return `<li><strong>${escapeHtml(kind)}</strong>${
              detail ? ` — ${escapeHtml(detail)}` : ""
            }</li>`;
          })
          .join("")}</ul>`
      : "";

  return `<div class="coverage-block">${matrixHtml}${flagsHtml}</div>`;
}

/** Varlık grafiği — kim/ne, hangi kanıtla. */
function entityGraphHtml(verdict: Verdict): string {
  const g = coerceEntityGraph(verdict.entity_graph);
  if (!g) return "";
  const nodes = g.nodes.filter((n) => !n.rejected).slice(0, 40);
  if (nodes.length === 0 && g.edges.length === 0) return "";
  return `
<div class="entity-block">
  ${
    nodes.length > 0
      ? `<table class="kpi-table">
          <thead><tr><th>Varlık</th><th>Tür</th><th>Önem</th><th>Güvenilirlik</th><th>Kanıt</th></tr></thead>
          <tbody>${nodes
            .map(
              (n) => `<tr>
              <td>${escapeHtml(n.canonical)}</td>
              <td>${escapeHtml(n.type || "—")}</td>
              <td>${Math.round(n.salience * 100)}%</td>
              <td>${Math.round(n.reliability * 100)}%</td>
              <td>${n.evidenceUrls.length}</td>
            </tr>`,
            )
            .join("")}</tbody>
        </table>`
      : ""
  }
  ${
    g.edges.length > 0
      ? `<h4>İlişkiler · ${g.edges.length}</h4>
        <ul class="assumptions">${g.edges
          .slice(0, 40)
          .map(
            (e) =>
              `<li>${escapeHtml(e.src)} <em>${escapeHtml(e.relation || "→")}</em> ${escapeHtml(
                e.dst,
              )} · güven ${Math.round(e.confidence * 100)}%</li>`,
          )
          .join("")}</ul>`
      : ""
  }
</div>`;
}

/** Soruşturma defteri — hangi hücre kapandı, hangi soru açık kaldı. */
function investigationHtml(verdict: Verdict): string {
  const plan = coerceInvestigationPlan(verdict.investigation_plan);
  if (!plan) return "";
  const STATUS_LABEL: Record<string, string> = {
    open: "açık",
    partial: "kısmen",
    covered: "kapandı",
    no_evidence: "kanıt yok",
  };
  return `
<div class="investigation-block">
  <h4>Soruşturma defteri · kapsam ${Math.round(plan.coverageScore * 100)}%</h4>
  <table class="kpi-table">
    <thead><tr><th>Varlık</th><th>Yön</th><th>Durum</th><th>Açık soru</th><th>Atıf</th></tr></thead>
    <tbody>${plan.cells
      .map(
        (c) => `<tr>
        <td>${escapeHtml(c.entity)}${c.mustCover ? " *" : ""}</td>
        <td>${escapeHtml(c.aspect || "—")}</td>
        <td><span class="risk-level risk-${
          c.status === "covered" ? "low" : c.status === "partial" ? "medium" : "high"
        }">${escapeHtml(STATUS_LABEL[c.status] ?? c.status)}</span></td>
        <td>${escapeHtml(c.openQuestions.join(" · ") || "—")}</td>
        <td>${c.citationUrls.length}</td>
      </tr>`,
      )
      .join("")}</tbody>
  </table>
  <p class="risk-note">* işaretli hücreler zorunlu kapsam maddesidir.</p>
</div>`;
}

export function buildVerdictReportHtml(input: VerdictReportInput): string {
  const {
    query,
    topology,
    startedAt,
    verdict,
    personas,
    turns,
    metrics,
    sources,
    ragCitations,
    consensusScore,
    questionQuality,
  } = input;
  const mode: ReportMode = input.mode ?? "full";
  const generatedAt = verdict.generatedAt;
  const topologyLabel = TOPOLOGY_LABELS[topology] ?? topology;
  const consensusPct =
    typeof consensusScore === "number" ? Math.round(consensusScore * 100) : null;

  const dissentRows =
    verdict.dissentingLog.length === 0
      ? `<p class="empty">Konsey kararına şerh sunulmadı.</p>`
      : `<table class="dissent">
          <thead>
            <tr><th>Üye</th><th>Şerh</th><th>Şiddet</th></tr>
          </thead>
          <tbody>
            ${verdict.dissentingLog
              .map((d) => {
                const p = personas.find((px) => px.id === d.agentId);
                const name = p ? p.displayName : d.agentId;
                return `<tr>
                  <td>${escapeHtml(name)}</td>
                  <td>${escapeHtml(d.concern)}</td>
                  <td><span class="badge severity-${d.severity}">${SEVERITY_LABELS[d.severity] ?? d.severity}</span></td>
                </tr>`;
              })
              .join("")}
          </tbody>
        </table>`;

  // Bölüm numaraları elle yazılıyordu ("01".."10") ve iki mod için ayrı
  // ayrı hesaplanıyordu; her yeni bölümde numaraları elle kaydırmak
  // gerekiyordu. Şimdi sayaç bölümün KENDİSİ basıldığında ilerliyor —
  // veri yoksa bölüm hiç basılmaz ve numara da tüketilmez.
  let sectionNo = 0;
  const section = (title: string, body: string, note?: string): string => {
    if (!body || body.trim() === "") return "";
    sectionNo += 1;
    const num = String(sectionNo).padStart(2, "0");
    return `
    <h2><span class="num">${num}</span>${title}</h2>
    ${note ? `<p class="section-note">${note}</p>` : ""}
    ${body}
`;
  };

  // ── YÖNETİCİ RAPORU ──────────────────────────────────────────────
  // Karar vericinin okuması gereken her şey. Web panelinde başlık
  // şeridinde ve sekmelerde duran karar katmanları (BLUF, güven, onay
  // kapısı, karar paketi, kapsam) buraya taşındı: rapor ekranla aynı
  // kararı anlatmalı, eksiğini değil.
  const executiveBody =
    mode === "detail"
      ? ""
      : [
          section("Karar Özü (BLUF)", blufHtml(verdict)),
          section("Karar Durumu ve Güven", decisionStatusHtml(verdict)),
          section(
            "Yönetici Özeti",
            `<p class="lead">${escapeHtml(verdict.executiveSummary)}</p>`,
          ),
          section(
            "Eylem Planı (5-Ufuk · SMART · RACI)",
            actionPlanHtml(verdict.actionPlan),
          ),
          section(
            "Riskler ve Plan Boşlukları",
            `${risksSectionHtml(verdict)}${simulationHtml(verdict)}${resilienceHtml(verdict)}`,
          ),
          section("Karar Paketi — Yönetim Özeti", boardBriefHtml(verdict)),
          section("Karar Hafızası", decisionMemoryHtml(verdict)),
          section("Muhalefet Şerhi", dissentRows),
          section("Kanıt Künyesi", evidenceBibliographyHtml(verdict)),
          section("Kapsam ve Tutarlılık", coverageHtml(verdict)),
          section("Editoryal Yorum", commentaryHtml(verdict)),
        ].join("");

  // ── DETAY RAPORU ─────────────────────────────────────────────────
  // Denetim izi: kim ne dedi, hangi kaynağa dayandı, nasıl dağıldı.
  const detailBody =
    mode === "executive"
      ? ""
      : [
          section("Konuşma Dağılımı", speakerShareHtml(metrics)),
          section(
            "Konsey Üyeleri",
            `<ul class="personas">${personasHtml(personas, metrics)}</ul>`,
          ),
          section("Web Kaynakçası", sourcesBibliographyHtml(sources)),
          section("Belge Atıfları (RAG)", ragBibliographyHtml(ragCitations)),
          section("Varlık Grafiği", entityGraphHtml(verdict)),
          section("Soruşturma Defteri", investigationHtml(verdict)),
          section("Tartışma Transkripti", transcriptHtml(personas, turns)),
          section(
            "Konsey Üyelerinin Bilişsel Profili",
            brainProfilesHtml(input.brainProfiles),
          ),
        ].join("");

  const bodySections = `${executiveBody}${detailBody}`;

  return `<!doctype html>
<html lang="tr">
<head>
<meta charset="utf-8" />
<title>Konsey Kararı — ${escapeHtml(query.slice(0, 80))}</title>
<style>
  :root {
    --accent: #7c5cff;
    --accent-soft: #b3a3ff;
    --bg: #ffffff;
    --fg: #14161d;
    --fg-muted: #4a5066;
    --fg-subtle: #7a8095;
    --line: #e2e5ee;
    --surface-soft: #f6f7fb;
    --severity-minor: #16a34a;
    --severity-major: #d97706;
    --severity-blocker: #dc2626;
  }
  * { box-sizing: border-box; }
  html, body {
    margin: 0;
    background: var(--bg);
    color: var(--fg);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Inter",
      "Helvetica Neue", Arial, sans-serif;
    font-size: 13px;
    line-height: 1.6;
  }
  .page {
    max-width: 880px;
    margin: 0 auto;
    padding: 56px 64px 80px;
  }
  .brand {
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-bottom: 1px solid var(--line);
    padding-bottom: 18px;
    margin-bottom: 28px;
  }
  .brand-mark {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .brand-logos {
    display: flex;
    align-items: center;
    gap: 14px;
  }
  .logo-crudfab {
    height: 52px;
    width: auto;
    object-fit: contain;
  }
  .logo-agenfab {
    height: 42px;
    width: auto;
  }
  .brand-divider {
    width: 1px;
    height: 44px;
    background: var(--line);
  }
  .brand-mark .label {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    color: var(--fg-subtle);
  }
  .brand-mark .product {
    font-size: 16px;
    font-weight: 600;
    color: var(--fg);
  }
  .brand .meta {
    text-align: right;
    font-size: 11px;
    color: var(--fg-subtle);
  }
  .brand .meta strong { color: var(--fg); display: block; font-size: 12px; }

  h1 {
    font-size: 28px;
    font-weight: 700;
    letter-spacing: -0.012em;
    margin: 0 0 6px;
    line-height: 1.2;
  }
  h1 .eyebrow {
    display: block;
    font-size: 10.5px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.32em;
    color: var(--accent);
    margin-bottom: 8px;
  }
  .query {
    background: var(--surface-soft);
    border-left: 3px solid var(--accent);
    padding: 14px 18px;
    margin: 16px 0 28px;
    border-radius: 4px;
    font-size: 13.5px;
    color: var(--fg);
  }

  .meta-grid {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 10px;
    margin-bottom: 32px;
  }
  .dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    margin-right: 7px;
    vertical-align: middle;
  }
  .muted { color: var(--fg-subtle); font-weight: 400; }
  .bib-domain { background: var(--accent); color: #fff; }
  .bib-rag { background: #0ea5e9; color: #fff; }
  ol.rag-bib .rag-title {
    color: var(--fg);
    font-weight: 600;
    font-size: 12.5px;
    word-break: break-word;
  }
  ol.rag-bib .bib-summary {
    font-style: italic;
  }
  ol.bibliography {
    list-style: none;
    padding: 0;
    margin: 12px 0 0;
    counter-reset: bib;
  }
  ol.bibliography li {
    display: flex;
    gap: 12px;
    padding: 10px 0;
    border-bottom: 1px solid var(--line);
  }
  ol.bibliography .bib-num {
    flex: 0 0 28px;
    font-variant-numeric: tabular-nums;
    color: var(--accent);
    font-weight: 700;
    font-size: 12px;
  }
  ol.bibliography .bib-body { flex: 1; min-width: 0; }
  ol.bibliography a {
    color: var(--fg);
    font-weight: 600;
    text-decoration: none;
    font-size: 12.5px;
    word-break: break-word;
  }
  ol.bibliography a:hover { color: var(--accent); }
  ol.bibliography .bib-meta {
    margin-top: 3px;
    font-size: 11px;
    color: var(--fg-muted);
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    align-items: center;
  }
  ol.bibliography .bib-summary {
    margin-top: 4px;
    font-size: 11.5px;
    color: var(--fg-muted);
    line-height: 1.5;
  }
  .persona-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 8px;
  }
  .persona-ceiling {
    font-size: 9.5px;
    font-variant-numeric: tabular-nums;
    background: var(--accent);
    color: #fff;
    padding: 2px 7px;
    border-radius: 999px;
    font-weight: 700;
    letter-spacing: 0.05em;
  }
  .bio-row {
    display: flex;
    gap: 6px;
    margin-top: 4px;
    font-size: 10.5px;
    line-height: 1.4;
  }
  .bio-k {
    flex: 0 0 100px;
    color: var(--fg-subtle);
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
  .bio-v {
    flex: 1;
    color: var(--fg-muted);
    word-break: break-word;
  }
  .bio-sys .bio-v {
    font-style: italic;
  }
  .meta-card {
    border: 1px solid var(--line);
    border-radius: 6px;
    padding: 10px 12px;
    background: var(--surface-soft);
  }
  .meta-card .k {
    font-size: 9.5px;
    text-transform: uppercase;
    letter-spacing: 0.22em;
    color: var(--fg-subtle);
    margin-bottom: 3px;
  }
  .meta-card .v {
    font-size: 13px;
    font-weight: 600;
    color: var(--fg);
  }

  h2 {
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.005em;
    margin: 32px 0 12px;
    padding-bottom: 6px;
    border-bottom: 1px solid var(--line);
  }
  h2 .num {
    color: var(--accent);
    margin-right: 8px;
    font-variant-numeric: tabular-nums;
  }
  h3 {
    font-size: 12.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    color: var(--fg-muted);
    margin: 20px 0 8px;
  }

  p { margin: 8px 0; color: var(--fg); }
  .lead { font-size: 14px; line-height: 1.7; color: var(--fg); }

  ul {
    list-style: none;
    padding: 0;
    margin: 8px 0;
  }
  ul li {
    position: relative;
    padding-left: 18px;
    margin-bottom: 6px;
  }
  ul li::before {
    content: "";
    position: absolute;
    left: 0;
    top: 9px;
    width: 6px;
    height: 6px;
    border-radius: 2px;
    background: var(--accent);
  }
  .empty {
    color: var(--fg-subtle);
    font-style: italic;
  }

  /* ────── 2026 Action Plan (5-Horizon · SMART · RACI) ────── */
  .action-plan { margin-top: 8px; }
  .ap-header {
    display: flex; flex-wrap: wrap; gap: 12px;
    align-items: center; justify-content: space-between;
    padding: 10px 14px; margin-bottom: 16px;
    border: 1px solid var(--line); border-radius: 8px;
    background: var(--surface-1);
    font-size: 12px;
  }
  .ap-summary strong { color: var(--accent); }
  .ap-legend {
    margin: -8px 0 16px; padding: 6px 12px;
    border: 1px dashed var(--line); border-radius: 6px;
    font-size: 10.5px; line-height: 1.6; color: var(--fg-subtle);
    background: var(--surface-1);
  }
  .ap-legend strong { color: var(--fg-muted); }
  .qq-band {
    display: flex; align-items: flex-start; gap: 12px;
    margin: 10px 0 18px; padding: 10px 14px;
    border: 1px solid var(--line); border-left-width: 4px; border-radius: 8px;
    background: var(--surface-1);
  }
  .qq-score { font-size: 22px; font-weight: 800; line-height: 1; }
  .qq-unit { font-size: 11px; font-weight: 600; opacity: 0.7; }
  .qq-text { font-size: 11px; line-height: 1.6; color: var(--fg-subtle); }
  .qq-text strong { color: var(--fg-muted); }
  .risk-block { margin-bottom: 18px; }
  .risk-block h3 {
    font-size: 13px; margin: 0 0 4px; color: var(--fg-strong);
  }
  /* ── Görev 18-24 + Karar Paketi yüzeyleri ────────────────────── */
  .section-note {
    margin: -4px 0 10px; font-size: 10.5px; line-height: 1.6;
    color: var(--fg-subtle);
  }
  .bluf {
    margin: 0 0 6px; padding: 14px 16px;
    border: 1px solid var(--accent); border-radius: 10px;
    background: linear-gradient(135deg, rgba(124,92,255,0.07), rgba(124,92,255,0.02));
    break-inside: avoid;
  }
  .bluf-eyebrow {
    font-size: 9.5px; font-weight: 700; letter-spacing: 0.09em;
    text-transform: uppercase; color: var(--accent);
  }
  .bluf-rec {
    margin: 4px 0 0; font-size: 15px; font-weight: 700; line-height: 1.5;
    color: var(--fg);
  }
  .bluf-reasons { margin: 8px 0 0; padding-left: 18px; }
  .bluf-reasons li {
    font-size: 11.5px; line-height: 1.65; color: var(--fg-muted);
    margin-bottom: 2px;
  }
  .bluf-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 10px; }
  .bluf-reversal {
    margin: 8px 0 0; font-size: 10.5px; line-height: 1.6; color: var(--fg-subtle);
  }
  .kp-chip {
    display: inline-block; padding: 3px 9px; border-radius: 999px;
    font-size: 10px; font-weight: 600;
    border: 1px solid var(--line); background: var(--surface-soft);
    color: var(--fg-muted);
  }
  .kp-chip.chip-ok {
    border-color: var(--severity-minor); color: var(--severity-minor);
    background: rgba(22, 163, 74, 0.07);
  }
  .kp-chip.chip-warn {
    border-color: var(--severity-major); color: var(--severity-major);
    background: rgba(217, 119, 6, 0.07);
  }
  .kp-chip.chip-bad {
    border-color: var(--severity-blocker); color: var(--severity-blocker);
    background: rgba(220, 38, 38, 0.07);
  }
  .decision-status { list-style: none; margin: 0; padding: 0; }
  .decision-status li {
    display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px;
    padding: 7px 0; border-bottom: 1px solid var(--line);
  }
  .decision-status li:last-child { border-bottom: 0; }
  .ds-detail { font-size: 10.5px; line-height: 1.6; color: var(--fg-subtle); }
  .ds-detail strong { color: var(--fg-muted); }
  .cited-block, .coverage-block, .entity-block,
  .investigation-block, .decision-memory, .resilience { margin-bottom: 18px; }
  .cited-block h3, .decision-memory h3, .resilience h3 {
    font-size: 13px; margin: 0 0 4px; color: var(--fg);
  }
  .board-brief h4, .decision-memory h4, .coverage-block h4,
  .entity-block h4, .investigation-block h4 {
    font-size: 11.5px; margin: 14px 0 6px; color: var(--fg);
    text-transform: uppercase; letter-spacing: 0.05em;
  }
  .bb-rec {
    padding: 10px 14px; margin-bottom: 12px;
    border: 1px solid var(--accent); border-radius: 8px;
    background: rgba(124, 92, 255, 0.05);
  }
  .bb-cap {
    font-size: 9.5px; font-weight: 700; letter-spacing: 0.08em;
    text-transform: uppercase; color: var(--accent);
  }
  .bb-rec p { margin: 4px 0 0; font-size: 12px; line-height: 1.65; }
  .bb-ask { color: var(--fg-muted); font-size: 11px !important; }
  .bb-roles { display: flex; flex-wrap: wrap; gap: 6px; }
  .res-scores {
    display: flex; align-items: center; flex-wrap: wrap; gap: 10px;
    margin-bottom: 10px;
  }
  .res-box {
    display: flex; flex-direction: column; gap: 2px;
    padding: 7px 12px; border: 1px solid var(--line); border-radius: 8px;
    background: var(--surface-soft);
  }
  .res-box.res-better {
    border-color: var(--severity-minor); background: rgba(22, 163, 74, 0.07);
  }
  .res-cap {
    font-size: 9px; letter-spacing: 0.07em; text-transform: uppercase;
    color: var(--fg-subtle);
  }
  .res-val {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 15px; font-weight: 700; color: var(--fg);
  }
  .res-arrow { color: var(--fg-subtle); font-size: 14px; }
  .risk-note {
    margin: 0 0 8px; font-size: 10.5px; line-height: 1.6;
    color: var(--fg-subtle);
  }
  .ap-framework {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px; letter-spacing: 0.06em; text-transform: uppercase;
    color: var(--fg-subtle);
    padding: 4px 8px; border-radius: 999px;
    background: rgba(124, 58, 237, 0.08);
    border: 1px solid rgba(124, 58, 237, 0.2);
  }
  .ap-tier { margin-bottom: 16px; break-inside: avoid; }
  .ap-tier h3 {
    display: flex; align-items: baseline; gap: 8px;
    border-bottom: 1px solid var(--line);
    padding-bottom: 4px; margin-bottom: 8px;
  }
  .ap-tier h3 .ap-sub {
    font-weight: normal; font-size: 11px; color: var(--fg-subtle);
  }
  .ap-tier h3 .ap-count {
    margin-left: auto; font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 11px; padding: 2px 8px; border-radius: 4px;
    background: rgba(124, 58, 237, 0.1); color: var(--accent);
  }

  ol.action-items {
    list-style: none; padding: 0; margin: 0;
    counter-reset: ai-counter;
  }
  ol.action-items > li {
    position: relative; padding: 10px 12px 10px 38px;
    margin-bottom: 8px; break-inside: avoid;
    border: 1px solid var(--line); border-radius: 6px;
    background: var(--surface-1);
    counter-increment: ai-counter;
  }
  ol.action-items > li::before {
    content: counter(ai-counter);
    position: absolute; left: 10px; top: 10px;
    width: 20px; height: 20px; border-radius: 4px;
    background: rgba(124, 58, 237, 0.12); color: var(--accent);
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 10px; font-weight: bold;
    display: flex; align-items: center; justify-content: center;
  }
  .ai-head {
    display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px;
  }
  .ai-pri {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 9px; font-weight: bold;
    padding: 2px 6px; border-radius: 3px;
    background: rgba(56, 189, 248, 0.15); color: #0ea5e9;
  }
  .ai-pri-P0 { background: rgba(244, 63, 94, 0.18); color: #be123c; }
  .ai-pri-P2 { background: rgba(148, 163, 184, 0.15); color: #475569; }
  .ai-eff {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 9px; padding: 2px 6px; border-radius: 3px;
    background: rgba(245, 158, 11, 0.15); color: #b45309;
  }
  .ai-title {
    flex: 1; min-width: 240px;
    font-size: 12.5px; font-weight: 600; color: var(--fg);
  }
  .ai-rationale {
    margin-top: 4px; font-size: 11px; line-height: 1.55;
    color: var(--fg-subtle); font-style: italic;
  }
  .ai-meta {
    margin-top: 6px; font-size: 10.5px; color: var(--fg-subtle);
    display: flex; flex-wrap: wrap; gap: 4px 10px;
  }
  .ai-meta strong { color: var(--fg); margin-right: 2px; }
  .ai-item-risks {
    margin-top: 6px;
    padding: 6px 10px;
    border-left: 2px solid var(--severity-blocker);
    background: rgba(220,38,38,0.04);
    border-radius: 0 4px 4px 0;
    font-size: 11px;
  }
  .ai-item-risks em { color: var(--severity-blocker); font-style: normal; font-weight: bold; }
  .ai-item-risks ul { margin: 4px 0 0 18px; padding: 0; }
  .ai-item-risks li { margin: 2px 0; color: var(--fg-muted); }

  .ai-deps {
    margin-top: 4px; font-size: 10.5px; color: var(--fg-subtle);
  }
  .ai-deps em { color: var(--accent); font-style: normal; font-weight: bold; }

  .ap-annex {
    margin-top: 18px; padding: 12px 14px; break-inside: avoid;
    border: 1px solid var(--line); border-radius: 6px;
    background: var(--surface-1);
  }
  .ap-annex h3 {
    margin-top: 0; margin-bottom: 10px;
    font-size: 13px; color: var(--accent);
  }
  table.risk-table, table.kpi-table {
    width: 100%; border-collapse: collapse; font-size: 11px;
  }
  table.risk-table th, table.kpi-table th,
  table.risk-table td, table.kpi-table td {
    padding: 6px 8px; text-align: left;
    border: 1px solid var(--line);
  }
  table.risk-table th, table.kpi-table th {
    background: rgba(0, 0, 0, 0.03); font-weight: 600;
  }
  .risk-level {
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 9px; padding: 2px 6px; border-radius: 3px;
    text-transform: uppercase; letter-spacing: 0.04em;
  }
  .risk-low { background: rgba(34, 197, 94, 0.18); color: #15803d; }
  .risk-medium { background: rgba(245, 158, 11, 0.18); color: #b45309; }
  .risk-high { background: rgba(249, 115, 22, 0.20); color: #c2410c; }
  .risk-critical { background: rgba(244, 63, 94, 0.20); color: #be123c; }

  ul.assumptions { list-style: none; padding: 0; }
  ul.assumptions li { padding-left: 18px; }
  ul.assumptions li::before {
    background: #f59e0b !important;
  }

  ul.personas {
    columns: 2;
    column-gap: 28px;
    margin-top: 12px;
  }
  ul.personas .persona {
    break-inside: avoid;
    border: 1px solid var(--line);
    border-radius: 6px;
    padding: 8px 12px;
    margin-bottom: 8px;
    background: var(--surface-soft);
    padding-left: 12px;
  }
  ul.personas .persona::before { display: none; }
  ul.personas .persona-name {
    font-weight: 600;
    color: var(--fg);
    font-size: 12.5px;
  }
  ul.personas .persona-role {
    color: var(--fg-muted);
    font-size: 11.5px;
  }

  table.dissent {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
    margin-top: 8px;
  }
  table.dissent th, table.dissent td {
    border-bottom: 1px solid var(--line);
    padding: 9px 10px;
    vertical-align: top;
    text-align: left;
  }
  table.dissent th {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--fg-subtle);
    border-bottom: 2px solid var(--line);
  }
  .badge {
    display: inline-block;
    padding: 2px 8px;
    border-radius: 999px;
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #fff;
  }
  .severity-minor { background: var(--severity-minor); }
  .severity-major { background: var(--severity-major); }
  .severity-blocker { background: var(--severity-blocker); }

  .turn {
    border-top: 1px solid var(--line);
    padding: 12px 0 4px;
  }
  .turn:first-of-type { border-top: 0; }
  .turn .turn-meta {
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: var(--accent);
    font-weight: 600;
  }
  .turn .role {
    color: var(--fg-subtle);
    font-weight: 500;
    text-transform: none;
    letter-spacing: 0;
  }
  .turn p {
    margin: 4px 0 10px;
    color: var(--fg);
    font-size: 12.5px;
  }

  /* ── Commentary ── */
  .commentary-block {
    border-radius: 6px;
    padding: 14px 18px;
    margin-bottom: 12px;
  }
  .commentary-analysis {
    border-left: 3px solid #0ea5e9;
    background: rgba(14,165,233,0.04);
  }
  .commentary-critique {
    border-left: 3px solid #f59e0b;
    background: rgba(245,158,11,0.04);
  }
  .commentary-header {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  }
  .commentary-icon { font-size: 14px; }
  .commentary-title {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--fg);
  }
  .commentary-block p {
    font-size: 12.5px;
    line-height: 1.7;
    color: var(--fg);
    margin: 0;
    white-space: pre-line;
  }

  /* ── Consensus banner ── */
  .consensus-banner {
    display: flex;
    align-items: center;
    gap: 20px;
    padding: 16px 20px;
    margin-bottom: 32px;
    border: 1px solid var(--accent);
    border-radius: 8px;
    background: linear-gradient(135deg, rgba(124,92,255,0.06), rgba(124,92,255,0.02));
  }
  .consensus-score {
    font-size: 42px;
    font-weight: 800;
    color: var(--accent);
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .consensus-unit { font-size: 20px; font-weight: 600; }
  .consensus-title {
    font-size: 13px;
    font-weight: 700;
    color: var(--fg);
    margin-bottom: 2px;
  }
  .consensus-desc {
    font-size: 11.5px;
    color: var(--fg-muted);
    line-height: 1.5;
  }

  /* ── Simulation ── */
  .sim-summary {
    display: flex;
    align-items: center;
    gap: 20px;
    margin-bottom: 16px;
  }
  .sim-score {
    flex: 0 0 auto;
    border: 2px solid;
    border-radius: 8px;
    padding: 10px 16px;
    text-align: center;
    min-width: 90px;
  }
  .sim-pct {
    font-size: 28px;
    font-weight: 800;
    line-height: 1;
    font-variant-numeric: tabular-nums;
  }
  .sim-frac {
    font-size: 10px;
    color: var(--fg-subtle);
    margin-top: 4px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  .sim-label {
    font-size: 10px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-top: 4px;
  }
  .sim-desc {
    flex: 1;
    font-size: 12px;
    color: var(--fg-muted);
    line-height: 1.6;
  }
  .sim-methodology {
    background: var(--surface-soft);
    border: 1px solid var(--line);
    border-radius: 6px;
    padding: 14px 18px;
    margin-bottom: 18px;
  }
  .sim-methodology h3 {
    margin: 0 0 8px;
    font-size: 11px;
  }
  .sim-methodology p {
    font-size: 12px;
    line-height: 1.65;
    color: var(--fg-muted);
    margin: 0 0 8px;
  }
  .sim-thresholds {
    display: flex;
    gap: 16px;
    margin-top: 10px;
    flex-wrap: wrap;
  }
  .sim-th {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--fg-muted);
    font-weight: 500;
  }
  .sim-table td:first-child { width: 28px; text-align: center; }
  .sim-icon {
    display: inline-flex;
    width: 18px;
    height: 18px;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    font-size: 10px;
    font-weight: 800;
  }
  .sim-pass { background: rgba(22,163,74,0.12); color: var(--severity-minor); }
  /* Açık kalan senaryo bir hata değil, plana eklenecek bir maddedir —
     kırmızı yerine uyarı tonu. */
  .sim-gap  { background: rgba(217,119,6,0.14); color: var(--severity-major); }

  /* ── Konsey Cognitive Renaissance — Bölüm D6 (Brain Profile) ── */
  .brain-cards {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
    gap: 12px;
    margin-top: 8px;
  }
  .brain-card {
    border: 1px solid var(--line);
    border-radius: 10px;
    padding: 12px 14px;
    background: linear-gradient(180deg, rgba(139,92,246,0.04), rgba(0,0,0,0));
    page-break-inside: avoid;
  }
  .brain-card-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 10px;
    padding-bottom: 8px;
    border-bottom: 1px dashed var(--line);
  }
  .brain-card-head strong { font-size: 12.5px; color: var(--fg-strong); }
  .brain-productivity {
    font-family: ui-monospace, SFMono-Regular, monospace;
    font-size: 13px;
    font-weight: 700;
  }
  .brain-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px 12px;
  }
  .brain-cell {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    font-size: 11px;
  }
  .brain-key { color: var(--fg-subtle); }
  .brain-val { font-family: ui-monospace, SFMono-Regular, monospace; color: var(--fg-strong); font-weight: 600; }

  /* ── Turn-level enrichments ── */
  .turn-critique {
    margin: 6px 0 8px;
    padding: 8px 12px;
    border-left: 3px solid var(--severity-major);
    background: rgba(217,119,6,0.06);
    border-radius: 0 4px 4px 0;
    font-size: 11.5px;
    color: var(--fg-muted);
  }
  .critique-label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.18em;
    color: var(--severity-major);
    margin-right: 8px;
  }
  .turn-thinking {
    margin: 4px 0 8px;
    border: 1px solid var(--line);
    border-radius: 4px;
    font-size: 11px;
  }
  .turn-thinking summary {
    cursor: pointer;
    padding: 6px 10px;
    font-weight: 600;
    color: var(--fg-subtle);
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
  }
  .turn-thinking p {
    padding: 0 10px 8px;
    font-style: italic;
    color: var(--fg-muted);
    font-size: 11px;
    line-height: 1.55;
  }
  .turn-research {
    margin: 4px 0 6px;
    padding: 6px 10px;
    background: var(--surface-soft);
    border-radius: 4px;
    font-size: 10.5px;
    color: var(--fg-muted);
    line-height: 1.5;
  }
  .research-label {
    font-size: 9px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.16em;
    color: var(--accent);
    margin-right: 8px;
  }
  .research-query {
    color: var(--fg);
    font-weight: 500;
    font-style: italic;
  }

  .footer {
    margin-top: 48px;
    padding-top: 16px;
    border-top: 1px solid var(--line);
    font-size: 10.5px;
    color: var(--fg-subtle);
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .toolbar {
    position: sticky;
    top: 12px;
    margin: 0 0 -32px auto;
    width: fit-content;
    display: flex;
    gap: 8px;
    z-index: 10;
  }
  .toolbar button {
    background: var(--accent);
    color: #fff;
    border: 0;
    padding: 8px 14px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 6px 16px -8px rgba(124, 92, 255, 0.5);
  }
  .toolbar button.ghost {
    background: var(--bg);
    color: var(--fg-muted);
    border: 1px solid var(--line);
    box-shadow: none;
  }

  @media print {
    @page { margin: 16mm 14mm; size: A4; }
    .toolbar { display: none; }
    .page { padding: 0; max-width: none; }
    .turn { break-inside: avoid; }
    h2 { break-after: avoid; }
    .meta-card { background: transparent; }
  }
</style>
</head>
<body>
  <div class="toolbar no-print">
    <button onclick="window.print()">Yazdır / PDF kaydet</button>
    <button class="ghost" onclick="window.close()">Kapat</button>
  </div>

  <div class="page">
    <header class="brand">
      <div class="brand-mark">
        <div class="brand-logos">
          <img src="${CRUDFAB_LOGO_DATA_URI}" alt="CrudFab" class="logo-crudfab" />
          ${AGENFAB_LOGO_SVG.replace(/<svg /, '<svg class="logo-agenfab" ')}
        </div>
        <div class="brand-divider"></div>
        <div>
          <div class="label">DAI Consensus · Konsey</div>
          <div class="product">Çok-Uzmanlı Karar Raporu</div>
        </div>
      </div>
      <div class="meta">
        <strong>${escapeHtml(fmtDate(generatedAt))}</strong>
        <span>Karar No: ${escapeHtml(String(generatedAt).slice(-8))}</span>
      </div>
    </header>

    <h1>
      <span class="eyebrow">Konsey Kararı</span>
      ${
        mode === "executive"
          ? "Yönetici Karar Raporu"
          : mode === "detail"
            ? "Detay ve Denetim Raporu"
            : "Tartışma Sonuç Raporu"
      }
    </h1>
    <div class="query">${escapeHtml(query)}</div>
    ${questionQualityHtml(questionQuality)}

    ${metricsGridHtml(topologyLabel, personas.length, turns, startedAt, generatedAt, metrics, sources, ragCitations)}

    ${consensusPct !== null ? `
    <div class="consensus-banner">
      <div class="consensus-score">${consensusPct}<span class="consensus-unit">%</span></div>
      <div class="consensus-body">
        <div class="consensus-title">Nihai Konsensüs Skoru</div>
        <div class="consensus-desc">${
          consensusPct >= 85
            ? "Yüksek mutabakat — konsey üyeleri kararın temel yönleri üzerinde güçlü uzlaşıya ulaştı."
            : consensusPct >= 65
              ? "Yakınsama — önemli konularda uzlaşı sağlandı, bazı muhalefet şerhleri mevcut."
              : "Düşük mutabakat — konsey üyeleri arasında önemli görüş ayrılıkları devam ediyor."
        }</div>
      </div>
    </div>` : ""}

    ${bodySections}

    <div class="footer">
      <span>DAI Consensus · Karar üreten yapay zekâ konseyi</span>
      <span>${escapeHtml(fmtDate(generatedAt))}</span>
    </div>
  </div>

  <!-- Yazici diyalogu otomatik acilmaz; ustteki dugme ile tetiklenir. -->
</body>
</html>`;
}

/**
 * Raporu yeni sekmede açar.
 *
 * Eskiden açılan sayfa ilk boyamadan hemen sonra `window.print()`
 * çağırıyordu: operatör "PDF Rapor"a bastığında raporu göremeden yazdırma
 * penceresiyle karşılaşıyor, okumak için önce onu kapatmak zorunda
 * kalıyordu. Artık rapor normal bir sayfa gibi açılıyor; yazdırmak
 * isteyen üstteki düğmeyi kullanır.
 */
export function openVerdictReport(input: VerdictReportInput): void {
  const html = buildVerdictReportHtml(input);
  const blob = new Blob([html], { type: "text/html;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener");
  if (!win) {
    // Pop-up blocked — fall back to download.
    const a = document.createElement("a");
    a.href = url;
    a.download = `konsey-karari-${input.verdict.generatedAt}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
  // Revoke after a generous delay so the new tab has time to load.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}
